// MobileInboxTab.jsx — mobile-only Inbox tab: large list of conversations
// with a push-style conversation view. Mirrors the Flutter messages screen.
import { useEffect, useMemo, useRef, useState } from "react"
import toast from "react-hot-toast"
import { ArrowLeft, ArrowUp, MessageCircle, WifiOff } from "lucide-react"
import { collection, doc as fsDoc, getDocs, setDoc } from "firebase/firestore"
import { db } from "../../firebaseConfig"
import { initials } from "../JobCard"
import { API_BASE, normalizeConv, normalizeMsg } from "./InboxTab"
import MobileBottomNav from "./MobileBottomNav"

const FF = '"Plus Jakarta Sans", "Inter", -apple-system, sans-serif'

// Flutter mobile palette
const INK          = "#0F172A"
const INK_MUTED    = "#64748B"
const INK_FAINT    = "#94A3B8"
const BG           = "#FAFAF7"
const SURFACE      = "#FFFFFF"
const SURFACE_ALT  = "#F3F2ED"
const BORDER_SOFT  = "#E6E5DF"
const ACCENT       = "#F97316"

const BRANDFETCH_CLIENT_ID = "1idn4glWklGs5yLvUdW"

// ── Date formatting ───────────────────────────────────────────────────────
function agoShort(dt) {
  if (!dt) return ""
  const diff = Date.now() - dt.getTime()
  if (diff < 60_000) return "now"
  const mins = Math.floor(diff / 60_000)
  if (mins < 60) return `${mins}m`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d`
  const weeks = Math.floor(days / 7)
  if (weeks < 4) return `${weeks}w`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months}mo`
  return `${Math.floor(days / 365)}y`
}
function timeLabel(dt) {
  if (!dt) return ""
  const h = dt.getHours() % 12 || 12
  const m = String(dt.getMinutes()).padStart(2, "0")
  const ampm = dt.getHours() < 12 ? "AM" : "PM"
  return `${h}:${m} ${ampm}`
}
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear()
      && a.getMonth()    === b.getMonth()
      && a.getDate()     === b.getDate()
}
function dateSeparator(dt) {
  const now = new Date()
  const diffDays = Math.floor((now - dt) / 86_400_000)
  if (sameDay(dt, now))  return "Today"
  if (diffDays === 1 || sameDay(dt, new Date(now.getTime() - 86_400_000))) return "Yesterday"
  if (diffDays < 7) return dt.toLocaleDateString("en-US", { weekday: "long" })
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: dt.getFullYear() === now.getFullYear() ? undefined : "numeric" })
}

// ── Company logo ──────────────────────────────────────────────────────────
function CompanyLogo({ company, logoUrl, size = 46 }) {
  const [bfErr, setBfErr] = useState(false)
  const [fbErr, setFbErr] = useState(false)
  const domain = company
    ? company.toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9]/g, "") + ".com"
    : null
  const bfUrl = domain ? `https://cdn.brandfetch.io/${domain}?c=${BRANDFETCH_CLIENT_ID}` : null
  const src = !bfErr ? bfUrl : (!fbErr && logoUrl ? logoUrl : null)
  const onErr = () => { if (!bfErr) setBfErr(true); else setFbErr(true) }

  const base = { width: size, height: size, borderRadius: 12, flexShrink: 0 }
  if (src) {
    return (
      <span style={{
        ...base, background: "#fff", border: `1px solid ${BORDER_SOFT}`,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden",
      }}>
        <img src={src} alt="" onError={onErr}
          style={{ width: "80%", height: "80%", objectFit: "contain" }} />
      </span>
    )
  }
  return (
    <span style={{
      ...base, background: SURFACE_ALT,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
    }}>
      <span style={{ fontSize: size * 0.3, fontWeight: 800, color: INK, letterSpacing: "-0.3px" }}>
        {initials(company)}
      </span>
    </span>
  )
}

// ── Conversation row ──────────────────────────────────────────────────────
function ConversationRow({ conv, currentUid, isUnread, onClick }) {
  const mine = conv.lastMessageBy === currentUid
  const firstName = (conv.recruiterName || "").split(" ")[0] || "They"
  const snippet = mine
    ? `You: ${conv.lastMessage}`
    : `${firstName}: ${conv.lastMessage}`
  return (
    <button
      onClick={onClick}
      style={{
        width: "100%", textAlign: "left", background: "transparent",
        border: "none", cursor: "pointer", fontFamily: FF,
        padding: "13px 16px",
        display: "flex", alignItems: "flex-start", gap: 13,
      }}
    >
      <CompanyLogo company={conv.companyName} logoUrl={conv.logoUrl} size={46} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{
            flex: 1, minWidth: 0,
            fontSize: 14, fontWeight: isUnread ? 700 : 600, color: INK,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {conv.recruiterName || "Someone"}
          </span>
          <span style={{
            fontSize: 11,
            color: isUnread ? ACCENT : INK_FAINT,
            fontWeight: isUnread ? 600 : 400,
            flexShrink: 0,
          }}>
            {agoShort(conv.lastMessageAt)}
          </span>
          {isUnread && (
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: ACCENT, flexShrink: 0 }} />
          )}
        </div>
        {conv.companyName && (
          <div style={{
            marginTop: 2, fontSize: 11, color: INK_FAINT, fontWeight: 500,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {conv.companyName}
          </div>
        )}
        <div style={{
          marginTop: 5, fontSize: 13,
          color: isUnread ? INK : INK_MUTED,
          fontWeight: isUnread ? 500 : 400,
          lineHeight: 1.4,
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>
          {snippet}
        </div>
      </div>
    </button>
  )
}

// ── Chat bubble ───────────────────────────────────────────────────────────
function Bubble({ msg, mine }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column",
      alignItems: mine ? "flex-end" : "flex-start",
      marginBottom: 6,
    }}>
      <div style={{
        maxWidth: "72%",
        padding: "10px 14px",
        background: mine ? INK : SURFACE,
        color: mine ? "#fff" : INK,
        borderTopLeftRadius:     mine ? 18 : 4,
        borderTopRightRadius:    mine ? 4  : 18,
        borderBottomLeftRadius:  18,
        borderBottomRightRadius: 18,
        border: mine ? "none" : `1px solid ${BORDER_SOFT}`,
        fontSize: 14, lineHeight: 1.5,
        whiteSpace: "pre-wrap", wordBreak: "break-word",
      }}>{msg.text}</div>
      <div style={{ marginTop: 3, fontSize: 10, color: INK_FAINT }}>
        {timeLabel(msg.sentAt)}
      </div>
    </div>
  )
}

function DateSeparator({ dt }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "16px 0" }}>
      <div style={{ flex: 1, height: 1, background: BORDER_SOFT }} />
      <span style={{ fontSize: 11, color: INK_FAINT, fontWeight: 500 }}>{dateSeparator(dt)}</span>
      <div style={{ flex: 1, height: 1, background: BORDER_SOFT }} />
    </div>
  )
}

// ── Conversation view (push screen) ───────────────────────────────────────
function ConversationView({ conv, currentUid, msgsCache, setMsgsCache, onBack }) {
  const [messages, setMessages] = useState(() => msgsCache[conv.id] || null)
  const [loading, setLoading] = useState(messages === null)
  const [error, setError]   = useState(null)
  const [text, setText]     = useState("")
  const [sending, setSending] = useState(false)
  const scrollRef = useRef(null)

  const scrollToBottom = (behavior = "smooth") => {
    const el = scrollRef.current
    if (!el) return
    requestAnimationFrame(() => el.scrollTo({ top: el.scrollHeight, behavior }))
  }

  useEffect(() => {
    let active = true
    const load = async () => {
      const cached = msgsCache[conv.id]
      if (cached) {
        setMessages(cached); setLoading(false)
        requestAnimationFrame(() => scrollToBottom("auto"))
      } else {
        setLoading(true)
      }
      try {
        const res = await fetch(
          `${API_BASE}/messages/conversations/${conv.id}/messages?uid=${encodeURIComponent(currentUid)}`
        )
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const body = await res.json()
        const list = (body.messages || []).map(normalizeMsg)
        if (!active) return
        setMessages(list)
        setMsgsCache(prev => ({ ...prev, [conv.id]: list }))
        setLoading(false)
        setError(null)
        requestAnimationFrame(() => scrollToBottom("auto"))
      } catch (err) {
        console.error("Fetch messages failed:", err)
        if (!active) return
        if (!cached) { setLoading(false); setError(String(err)) }
      }
    }
    load()
    return () => { active = false }
  }, [conv.id, currentUid])

  const send = async () => {
    const body = text.trim()
    if (!body || sending) return
    const now = new Date()
    const pending = {
      id: `pending-${now.getTime()}`,
      senderId: currentUid, text: body, sentAt: now, pending: true,
    }
    setText(""); setSending(true)
    setMessages(prev => {
      const next = [...(prev || []), pending]
      setMsgsCache(c => ({ ...c, [conv.id]: next }))
      return next
    })
    scrollToBottom()
    try {
      const res = await fetch(`${API_BASE}/messages/conversations/${conv.id}/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ senderId: currentUid, text: body }),
      })
      if (res.status !== 201) throw new Error(`HTTP ${res.status}`)
      const r2 = await fetch(
        `${API_BASE}/messages/conversations/${conv.id}/messages?uid=${encodeURIComponent(currentUid)}`
      )
      if (r2.ok) {
        const b2 = await r2.json()
        const list = (b2.messages || []).map(normalizeMsg)
        setMessages(list)
        setMsgsCache(c => ({ ...c, [conv.id]: list }))
        scrollToBottom()
      }
    } catch (err) {
      console.error("Send failed:", err)
      toast.error("Couldn't send. Try again.")
      setMessages(prev => {
        const next = (prev || []).filter(m => m.id !== pending.id)
        setMsgsCache(c => ({ ...c, [conv.id]: next }))
        return next
      })
      setText(body)
    } finally { setSending(false) }
  }

  const firstName = (conv.recruiterName || "").split(" ")[0] || "them"
  const canSend = text.trim().length > 0 && !sending

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 180,
      background: BG, color: INK, fontFamily: FF,
      display: "flex", flexDirection: "column",
    }}>
      <style>{`
        @keyframes minbox-spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* Header */}
      <div style={{
        padding: `calc(8px + env(safe-area-inset-top)) 12px 12px`,
        display: "flex", alignItems: "center", gap: 10,
        borderBottom: `1px solid ${BORDER_SOFT}`,
        background: BG,
      }}>
        <button
          onClick={onBack}
          aria-label="Back"
          style={{
            width: 36, height: 36, border: "none", background: "transparent",
            color: INK, cursor: "pointer",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <CompanyLogo company={conv.companyName} logoUrl={conv.logoUrl} size={36} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 15, fontWeight: 700, color: INK,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>{conv.recruiterName || "Conversation"}</div>
          {conv.companyName && (
            <div style={{
              fontSize: 12, color: INK_MUTED,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{conv.companyName}</div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} style={{
        flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch",
        padding: "16px 16px 12px",
      }}>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
            <span style={{
              width: 22, height: 22, borderRadius: "50%",
              border: `2px solid ${BORDER_SOFT}`, borderTopColor: INK,
              animation: "minbox-spin 0.8s linear infinite", display: "inline-block",
            }} />
          </div>
        ) : error ? (
          <div style={{ padding: 40, textAlign: "center", color: INK_FAINT, fontSize: 13 }}>
            Couldn't load messages.
          </div>
        ) : !messages || messages.length === 0 ? (
          <div style={{ padding: 60, textAlign: "center", color: INK_FAINT, fontSize: 13 }}>
            No messages yet — say hi.
          </div>
        ) : (
          messages.map((m, i) => {
            const prev = i > 0 ? messages[i - 1] : null
            const showSep = !prev || !sameDay(prev.sentAt, m.sentAt)
            return (
              <div key={m.id}>
                {showSep && <DateSeparator dt={m.sentAt} />}
                <Bubble msg={m} mine={m.senderId === currentUid} />
              </div>
            )
          })
        )}
      </div>

      {/* Compose */}
      <div style={{
        padding: `10px 12px calc(12px + env(safe-area-inset-bottom))`,
        borderTop: `1px solid ${BORDER_SOFT}`,
        background: BG,
      }}>
        <form
          onSubmit={e => { e.preventDefault(); send() }}
          style={{
            display: "flex", alignItems: "flex-end", gap: 6,
            padding: "4px 4px 4px 16px",
            background: SURFACE, borderRadius: 22,
            border: `1px solid ${BORDER_SOFT}`,
          }}
        >
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send() }
            }}
            placeholder={`Reply to ${firstName}…`}
            rows={1}
            style={{
              flex: 1, resize: "none", border: "none", outline: "none",
              background: "transparent", padding: "9px 0",
              fontSize: 14, lineHeight: 1.5, color: INK, fontFamily: FF,
              minHeight: 24, maxHeight: 140,
            }}
          />
          <button
            type="submit"
            disabled={!canSend}
            aria-label="Send"
            style={{
              width: 34, height: 34, borderRadius: 999,
              border: "none",
              background: canSend ? INK : SURFACE_ALT,
              color: canSend ? "#fff" : INK_FAINT,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              cursor: canSend ? "pointer" : "default",
              flexShrink: 0, margin: 6,
              boxShadow: canSend ? "0 2px 8px rgba(15,23,42,0.15)" : "none",
            }}
          >
            {sending ? (
              <span style={{
                width: 13, height: 13, borderRadius: "50%",
                border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff",
                animation: "minbox-spin 0.8s linear infinite", display: "inline-block",
              }} />
            ) : <ArrowUp size={16} strokeWidth={2.5} />}
          </button>
        </form>
      </div>
    </div>
  )
}

// ── Empty state ───────────────────────────────────────────────────────────
function EmptyState() {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      padding: "60px 40px", textAlign: "center",
    }}>
      <MessageCircle size={28} color={INK_FAINT} />
      <div style={{ marginTop: 14, fontSize: 15, fontWeight: 600, color: INK, letterSpacing: "-0.2px" }}>
        No messages yet
      </div>
      <p style={{ margin: "6px 0 0", fontSize: 13, color: INK_MUTED, lineHeight: 1.5, maxWidth: 280 }}>
        Replies from recruiters will show up here.
      </p>
    </div>
  )
}

// ── Row shimmer ───────────────────────────────────────────────────────────
function RowShimmer() {
  return (
    <div style={{
      display: "flex", gap: 13, padding: "13px 16px",
      borderBottom: `1px solid ${BORDER_SOFT}`,
    }}>
      <div style={{ width: 46, height: 46, borderRadius: 12, background: SURFACE_ALT }} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7, paddingTop: 4 }}>
        <div style={{ width: "55%", height: 11, borderRadius: 4, background: SURFACE_ALT }} />
        <div style={{ width: "80%", height: 10, borderRadius: 4, background: SURFACE_ALT }} />
        <div style={{ width: "40%", height: 9, borderRadius: 4, background: SURFACE_ALT }} />
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════════════════════════════════════
export default function MobileInboxTab({
  userId, onTabChange, activeTab, pendingApplications,
}) {
  const [conversations, setConversations] = useState([])
  const [loading, setLoading]  = useState(true)
  const [error, setError]      = useState(null)
  const [readAts, setReadAts]  = useState({})
  const [msgsCache, setMsgsCache] = useState({})
  const [selectedId, setSelectedId] = useState(null)

  const load = async () => {
    if (!userId) { setLoading(false); return }
    try {
      const res = await fetch(`${API_BASE}/messages/inbox?uid=${encodeURIComponent(userId)}`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const body = await res.json()
      const list = (body.conversations || []).map(normalizeConv)
      list.sort((a, b) => b.lastMessageAt - a.lastMessageAt)
      setConversations(list); setError(null)
    } catch (err) {
      console.error("Inbox fetch failed:", err)
      setError(String(err))
    } finally { setLoading(false) }
  }

  const loadReadAts = async () => {
    if (!userId) return
    try {
      const snap = await getDocs(collection(db, "users", userId, "inbox"))
      const next = {}
      snap.forEach(d => {
        const v = d.data()?.readAt
        if (v?.toDate) next[d.id] = v.toDate()
      })
      setReadAts(next)
    } catch (err) { console.error("Read-ats load failed:", err) }
  }

  useEffect(() => { load(); loadReadAts() }, [userId])

  const markRead = async (convId) => {
    const now = new Date()
    setReadAts(prev => ({ ...prev, [convId]: now }))
    if (!userId || !convId) return
    try {
      await setDoc(fsDoc(db, "users", userId, "inbox", convId), { readAt: now }, { merge: true })
    } catch (err) { console.error("markRead failed:", err) }
  }

  const isUnread = (c) => {
    if (!userId) return false
    if (c.lastMessageBy === userId) return false
    const r = readAts[c.id]
    return !r || c.lastMessageAt > r
  }

  const unreadTotal = useMemo(
    () => conversations.filter(isUnread).length,
    [conversations, readAts, userId]
  )

  const selectedConv = useMemo(
    () => conversations.find(c => c.id === selectedId) || null,
    [conversations, selectedId]
  )

  // Push conversation view when a row is tapped.
  if (selectedConv) {
    return (
      <ConversationView
        key={selectedConv.id}
        conv={selectedConv}
        currentUid={userId}
        msgsCache={msgsCache}
        setMsgsCache={setMsgsCache}
        onBack={() => setSelectedId(null)}
      />
    )
  }

  const total = conversations.length
  const subtitle = total === 0
    ? "No conversations yet"
    : unreadTotal > 0
      ? `${total} conversation${total === 1 ? "" : "s"} · ${unreadTotal} unread`
      : `${total} conversation${total === 1 ? "" : "s"}`

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 150,
      background: BG, color: INK, fontFamily: FF,
      display: "flex", flexDirection: "column",
    }}>
      <style>{`
        @keyframes minbox-shim { 0%,100% { opacity: 0.55; } 50% { opacity: 1; } }
      `}</style>

      <div style={{
        flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch",
        paddingTop: "calc(12px + env(safe-area-inset-top))",
        paddingBottom: 108,
      }}>
        {/* Header */}
        <div style={{ padding: "0 20px 12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h1 style={{
              margin: 0, fontSize: 30, fontWeight: 800, color: INK,
              letterSpacing: "-0.8px", lineHeight: 1.1,
            }}>Messages</h1>
            {unreadTotal > 0 && (
              <span style={{
                padding: "3px 9px", borderRadius: 999,
                background: ACCENT, color: "#fff",
                fontSize: 11, fontWeight: 700,
                boxShadow: "0 2px 10px rgba(249,115,22,0.25)",
              }}>{unreadTotal}</span>
            )}
          </div>
          <div style={{ marginTop: 4, fontSize: 12, color: INK_MUTED, fontWeight: 500 }}>
            {subtitle}
          </div>
        </div>

        {/* List */}
        {loading ? (
          <>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} style={{ animation: "minbox-shim 1.4s ease-in-out infinite" }}>
                <RowShimmer />
              </div>
            ))}
          </>
        ) : error ? (
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center",
            padding: "48px 40px", textAlign: "center", gap: 12,
          }}>
            <WifiOff size={32} color={INK_FAINT} />
            <div style={{ fontSize: 15, fontWeight: 700, color: INK }}>Couldn't load messages</div>
            <p style={{ margin: 0, fontSize: 13, color: INK_MUTED, maxWidth: 280 }}>{error}</p>
            <button
              onClick={() => { setLoading(true); load() }}
              style={{
                marginTop: 8,
                padding: "10px 22px", borderRadius: 10, border: "none",
                background: INK, color: "#fff",
                fontFamily: FF, fontSize: 13, fontWeight: 700, cursor: "pointer",
              }}
            >
              Try again
            </button>
          </div>
        ) : total === 0 ? (
          <EmptyState />
        ) : (
          <div>
            {conversations.map((c, i) => (
              <div
                key={c.id}
                style={{
                  borderBottom: i < conversations.length - 1 ? `1px solid ${BORDER_SOFT}` : "none",
                  marginLeft: 0,
                }}
              >
                <ConversationRow
                  conv={c}
                  currentUid={userId}
                  isUnread={isUnread(c)}
                  onClick={() => {
                    setSelectedId(c.id)
                    if (isUnread(c)) markRead(c.id)
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <MobileBottomNav
        activeTab={activeTab}
        onTabChange={onTabChange}
        pendingApplications={pendingApplications || 0}
      />
    </div>
  )
}
